import { useMemo, useState } from "react"
import type { AuditFieldConfig } from "@/lib/audit/audit-config"
import type { AuditHistoryFilters } from "@/types/ui/audit"
import type { AuditHistoryDocument } from "@/types/api/audit"
import { transformAuditHistory, type WorkerNameResolvers } from "@/lib/adapters/audit-history-adapter"
import { filterEvents } from "@/lib/audit/audit-filters"
import { HistoryFilters } from "./HistoryFilters"
import { ChangeHistoryTable } from "./ChangeHistoryTable"

interface HistoryContentProps {
  currentVersion: number
  config: AuditFieldConfig
  validEntityIds?: string[]
  getAuditByName: (id: number) => string
  auditHistory: AuditHistoryDocument[]
  resolveWorkerName?: WorkerNameResolvers
}

export function HistoryContent({ currentVersion, config, validEntityIds, getAuditByName, auditHistory, resolveWorkerName }: HistoryContentProps) {

  const [filters, setFilters] = useState<AuditHistoryFilters>({
    searchQuery: "",
    statusFilter: [],
    sectionFilter: [],
    userFilter: [],
    actionFilter: [],
    showSystemFields: false,
    hideDeletedVersions: true,
  })

  // Transform raw audit data to POC format
  const transformedEvents = useMemo(() => {
    return transformAuditHistory(auditHistory, config, validEntityIds, getAuditByName, resolveWorkerName)
  }, [auditHistory, config, validEntityIds, getAuditByName, resolveWorkerName])

  // Extract unique values for filter dropdowns
  const { uniqueStatuses, uniqueUsers, uniqueActions } = useMemo(() => {
    const statuses = new Set<string>()
    const users = new Set<string>()
    const actions = new Set<string>()

    transformedEvents.forEach((event) => {
      // Collect unique statuses, users, and actions from all audits within each version
      event.audits.forEach((audit) => {
        statuses.add(audit.status)
        users.add(audit.modifiedBy.displayName)
        actions.add(audit.action)
      })
    })

    return {
      uniqueStatuses: Array.from(statuses),
      uniqueUsers: Array.from(users),
      uniqueActions: Array.from(actions),
    }
  }, [transformedEvents])

  const filteredEvents = useMemo(() => {
    let events = filterEvents(transformedEvents, filters, config)
    
    // Filter out deleted versions if requested
    if (filters.hideDeletedVersions) {
      events = events.filter(event => !event.isDeleted)
    }
    
    return events
  }, [transformedEvents, filters, config])

  if (transformedEvents.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <p className="text-sm text-muted-foreground">No changes have been recorded yet</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">

      {/* Filters */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <HistoryFilters
            config={config}
            filters={filters}
            onFiltersChange={setFilters}
            totalResults={filteredEvents.length}
            uniqueStatuses={uniqueStatuses}
            uniqueUsers={uniqueUsers}
            uniqueActions={uniqueActions}
          />
        </div>
      </div>

      {/* Change History Table */}
      <div className="container mx-auto px-6 py-6">
        <ChangeHistoryTable events={filteredEvents} currentVersion={currentVersion} config={config} />
      </div>
    </div>
  )
}
